/*
CART DRAWER: HIDE CHECKOUT IF SHIP DATE IS REQUIRED

Purpose:
- If the cart contains Frozen items AND the ship date attribute (LF_CART_ATTRIBUTE_KEY) is empty:
  - Hide/disable the cart drawer checkout button
  - Insert (or show) a replacement CTA button that links to /cart
- If ship date is present (or no frozen items), restore normal cart drawer checkout.

Notes:
- Targets Dawn cart drawer checkout button via:
  - #CartDrawer-Checkout (primary)
  - .cart-drawer__footer button[name="checkout"] (fallback)
- Inserts a <a> button inside .cart-drawer__footer so it looks native.
- Runs on:
  - Page load
  - Dawn cart updates (Pub/Sub if available)
  - Cart drawer DOM mutations (footer re-render)
*/

(function () {
  "use strict";

  /* CONFIG */
  var LF_SETTINGS = window.LF_SHIP_SETTINGS || {};

  var LF_ALLOWED_WEEKDAYS = lfParseWeekdays(LF_SETTINGS.allowedWeekdays, [2, 3]);
  var LF_MIN_DAYS_FROM_TODAY = lfParseInt(LF_SETTINGS.minDaysFromToday, 2);
  var LF_MAX_DAYS_FROM_TODAY = lfParseInt(LF_SETTINGS.maxDaysFromToday, 14);
  var LF_BLACKOUT_DATES = lfParseIsoList(LF_SETTINGS.blackoutDates);
  var LF_WHITELIST_DATES = lfParseIsoList(LF_SETTINGS.whitelistDates);
  var LF_CART_ATTRIBUTE_KEY = LF_SETTINGS.cartAttributeKey || "ship_date";

  var LF_DRAWER_CTA_ID = "lf-cartdrawer-go-to-cart";
  var LF_DRAWER_BLOCKED_CLASS = "lf-cartdrawer-shipdate-blocked";

  function lfParseInt(value, fallback) {
    var n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function lfParseWeekdays(value, fallback) {
    if (!Array.isArray(value)) return fallback;
    return value
      .map(function (v) { return parseInt(v, 10); })
      .filter(function (v) { return Number.isFinite(v); });
  }

  function lfParseIsoList(value) {
    if (!Array.isArray(value)) return [];
    return value
      .map(function (v) { return String(v); })
      .filter(function (v) { return /^\d{4}-\d{2}-\d{2}$/.test(v); });
  }

  function lfStartOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  function lfAddDays(date, days) {
    var d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    d.setDate(d.getDate() + days);
    return d;
  }

  function lfToISO(date) {
    var y = date.getFullYear();
    var m = String(date.getMonth() + 1).padStart(2, "0");
    var d = String(date.getDate()).padStart(2, "0");
    return y + "-" + m + "-" + d;
  }

  function lfUpdateCartAttribute(value) {
    var body = { attributes: {} };
    body.attributes[LF_CART_ATTRIBUTE_KEY] = value || "";

    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json();
    });
  }

  function lfIsAllowedWeekday(date) {
    var day = date.getDay();
    return LF_ALLOWED_WEEKDAYS.indexOf(day) !== -1;
  }

  function lfIsSelectableDate(date) {
    var iso = lfToISO(date);

    if (LF_WHITELIST_DATES.indexOf(iso) !== -1) return true;

    var today = lfStartOfDay(new Date());
    var minDate = lfAddDays(today, Math.max(0, LF_MIN_DAYS_FROM_TODAY || 0));
    var maxDate = lfAddDays(today, Math.max(0, LF_MAX_DAYS_FROM_TODAY || 0));

    if (LF_MIN_DAYS_FROM_TODAY && date < minDate) return false;
    if (LF_MAX_DAYS_FROM_TODAY && date > maxDate) return false;
    if (LF_BLACKOUT_DATES.indexOf(iso) !== -1) return false;
    if (LF_ALLOWED_WEEKDAYS.length && !lfIsAllowedWeekday(date)) return false;

    return true;
  }

  function lfParseISODate(value) {
    if (!value || typeof value !== "string") return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;

    var parts = value.split("-");
    var y = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10);
    var d = parseInt(parts[2], 10);

    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;

    var date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() + 1 !== m || date.getDate() !== d) {
      return null;
    }

    return date;
  }

  function lfFindEarliestSelectableDate() {
    var today = lfStartOfDay(new Date());
    var maxDays = LF_MAX_DAYS_FROM_TODAY ? Math.max(0, LF_MAX_DAYS_FROM_TODAY) : 365;

    for (var i = 0; i <= maxDays; i++) {
      var candidate = lfAddDays(today, i);
      if (lfIsSelectableDate(candidate)) return candidate;
    }

    return null;
  }

  function lfApplyShipDateToInputs(iso) {
    var input = document.querySelector('[data-ship-week-input]');
    var hidden = document.querySelector('[data-ship-week-attr]');

    if (input && input._flatpickr) {
      input._flatpickr.setDate(iso, false);
    } else if (input) {
      input.value = iso;
    }

    if (hidden) hidden.value = iso;
  }

  function lfEnsureDefaultShipDate() {
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var attrs = cart.attributes || {};
        var current = attrs[LF_CART_ATTRIBUTE_KEY];
        var currentDate = lfParseISODate(current);

        if (currentDate && lfIsSelectableDate(currentDate)) return;

        var earliest = lfFindEarliestSelectableDate();
        if (!earliest) return;

        var iso = lfToISO(earliest);

        return lfUpdateCartAttribute(iso).then(function () {
          lfApplyShipDateToInputs(iso);
          lfEnforceShipDateRequirement();
          lfEnforceCartDrawerShipDateRequirement();
        });
      });
  }

  function lfCartHasFrozenItems() {
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) return false;

        return cart.items.some(function (item) {
          return item.product_type && item.product_type.toLowerCase() === "frozen";
        });
      });
  }

  function lfInitShipDatePicker() {
    var root = document.querySelector('[data-ship-week-root]');
    var input = document.querySelector('[data-ship-week-input]');
    var hidden = document.querySelector('[data-ship-week-attr]');

    if (!root || !input || !window.flatpickr) return;

    lfCartHasFrozenItems().then(function (hasFrozen) {
      if (!hasFrozen) {
        root.style.display = "none";
        return;
      }

      root.style.display = "";

      window.flatpickr(input, {
        dateFormat: "Y-m-d",

        disable: [
          function (date) {
            return !lfIsSelectableDate(date);
          },
        ],

        onReady: function () {
          if (hidden && hidden.value) {
            input.value = hidden.value;
          }
        },

        onChange: function (selectedDates) {
          if (!selectedDates || !selectedDates.length) return;

          var picked = lfStartOfDay(selectedDates[0]);
          if (!lfIsSelectableDate(picked)) return;

          var isoPicked = lfToISO(picked);

          input.value = isoPicked;
          if (hidden) hidden.value = isoPicked;

          lfUpdateCartAttribute(isoPicked).then(function () {
            lfEnforceShipDateRequirement();
            lfEnforceCartDrawerShipDateRequirement();
          });
        },
      });
    });

    var clearBtn = document.querySelector('[data-ship-week-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        if (input && input._flatpickr) {
          input._flatpickr.clear();
        }

        if (input) input.value = '';
        if (hidden) hidden.value = '';

        lfUpdateCartAttribute('').then(function () {
          lfEnforceShipDateRequirement();
          lfEnforceCartDrawerShipDateRequirement();
        });
      });
    }
  }

  function lfClearShipDateIfNoFrozen() {
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) return;

        var hasFrozen = cart.items.some(function (item) {
          return item.product_type && item.product_type.toLowerCase() === "frozen";
        });

        if (hasFrozen) return;

        var root = document.querySelector('[data-ship-week-root]');

        if (root) root.style.display = "none";
      });
  }

  function lfBindCartChangeEvents() {
    function runNow() {
      lfClearShipDateIfNoFrozen();
      lfEnforceShipDateRequirement();
      lfEnforceCartDrawerShipDateRequirement();
    }

    var itemsEl = document.querySelector(".cart__items");
    if (!itemsEl) {
      runNow();
      return;
    }

    var hadDisabled = itemsEl.classList.contains("cart__items--disabled");

    var obs = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type !== "attributes") continue;
        if (mutations[i].attributeName !== "class") continue;

        var hasDisabled = itemsEl.classList.contains("cart__items--disabled");

        if (hadDisabled && !hasDisabled) {
          runNow();
        }

        hadDisabled = hasDisabled;
      }
    });

    obs.observe(itemsEl, { attributes: true, attributeFilter: ["class"] });

    runNow();
  }

  function lfEnforceShipDateRequirement() {
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var checkoutBtn = document.getElementById("checkout");
        if (!checkoutBtn) return;

        var hasFrozen = cart.items && cart.items.some(function (item) {
          return item.product_type && item.product_type.toLowerCase() === "frozen";
        });

        var shipDate = cart.attributes && cart.attributes[LF_CART_ATTRIBUTE_KEY];
        var mustBlock = hasFrozen && !shipDate;

        if (mustBlock) {
          document.body.classList.add('lf-shipdate-blocked');

          checkoutBtn.disabled = true;
          checkoutBtn.classList.add("is-disabled-by-ship-week");
          checkoutBtn.setAttribute("aria-disabled", "true");
          checkoutBtn.title = "Please select a ship date before checkout";
          checkoutBtn.textContent = "Select a shipping date";
        } else {
          document.body.classList.remove('lf-shipdate-blocked');

          checkoutBtn.disabled = false;
          checkoutBtn.classList.remove("is-disabled-by-ship-week");
          checkoutBtn.removeAttribute("aria-disabled");
          checkoutBtn.removeAttribute("title");
          checkoutBtn.textContent = "Check out";
        }
      });
  }

  function lfGetCartDrawerCheckoutButton() {
    return document.getElementById("CartDrawer-Checkout") ||
      document.querySelector(".cart-drawer__footer button[name='checkout']");
  }

  function lfEnsureCartDrawerCta(footerEl) {
    if (!footerEl) return null;

    var existing = document.getElementById(LF_DRAWER_CTA_ID);
    if (existing) return existing;

    var a = document.createElement("a");
    a.id = LF_DRAWER_CTA_ID;
    a.href = "/cart";
    a.className = "button button--primary button--full-width";
    a.textContent = "Go to Cart to Select Ship Date";

    footerEl.appendChild(a);
    return a;
  }

  function lfEnforceCartDrawerShipDateRequirement() {
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var hasFrozen = cart.items && cart.items.some(function (item) {
          return item.product_type && item.product_type.toLowerCase() === "frozen";
        });

        var shipDate = cart.attributes && cart.attributes[LF_CART_ATTRIBUTE_KEY];
        var mustBlock = hasFrozen && !shipDate;

        var footer = document.querySelector(".cart-drawer__footer");
        var checkoutBtn = lfGetCartDrawerCheckoutButton();

        if (!footer || !checkoutBtn) return;

        var cta = lfEnsureCartDrawerCta(footer);

        if (mustBlock) {
          document.documentElement.classList.add(LF_DRAWER_BLOCKED_CLASS);

          checkoutBtn.style.display = "none";
          if (cta) cta.style.display = "";
        } else {
          document.documentElement.classList.remove(LF_DRAWER_BLOCKED_CLASS);

          checkoutBtn.style.display = "";
          if (cta) cta.style.display = "none";
        }
      });
  }

  function lfBindCartDrawerEvents() {
    function runNow() {
      lfEnforceCartDrawerShipDateRequirement();
    }

    // Run on load
    runNow();

    // Hook Dawn pub/sub if available
    try {
      if (typeof window.subscribe === "function" && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
          lfEnsureDefaultShipDate();
          setTimeout(runNow, 0);
        });
      }
    } catch (e) {}

    // Observe cart-drawer DOM changes because Dawn re-renders the footer often
    var drawer = document.querySelector("cart-drawer");
    if (!drawer) return;

    var obs = new MutationObserver(function () {
      runNow();
    });

    obs.observe(drawer, { childList: true, subtree: true });
  }

  document.addEventListener("DOMContentLoaded", function () {
    lfEnsureDefaultShipDate();
    lfInitShipDatePicker();
    lfBindCartChangeEvents();
    lfBindCartDrawerEvents();
  });

})();
